import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useMsg, type MaybeMsg } from '../i18n/msg';

export interface ToastProps {
  open: boolean;
  kind?: 'success' | 'error' | 'warning';
  /** Backend text as a string, app wording as a Msg (translated at render, so it follows a language switch). */
  message: MaybeMsg;
  retryLabel?: string;
  duration?: number;
  onRetry?: () => void;
  onClose: () => void;
}

export function Toast({
  open,
  kind = 'success',
  message,
  retryLabel,
  duration = 4000,
  onRetry,
  onClose,
}: ToastProps) {
  const { t } = useTranslation(['common', 'layout']);
  const tr = useMsg();
  useEffect(() => {
    if (!open || duration <= 0) return;
    const timer = window.setTimeout(onClose, duration);
    return () => window.clearTimeout(timer);
  }, [duration, onClose, open]);

  if (!open) return null;
  return (
    <div className={`toast toast--${kind}`} role={kind === 'error' ? 'alert' : 'status'}>
      <span className="toast__icon" aria-hidden={true}>{kind === 'success' ? '✓' : kind === 'warning' ? '⚠' : '!'}</span>
      <span className="toast__message">{tr(message)}</span>
      {onRetry ? <button type="button" className="toast__retry" onClick={onRetry}>{retryLabel ?? t('common:actions.retry')}</button> : null}
      <button type="button" className="toast__close" aria-label={t('layout:toast.close')} onClick={onClose}>×</button>
    </div>
  );
}

export default Toast;
